import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { updateCodingPhaseTime } from '@/api/interviewApi';

const CODING_PHASE_DURATION_SECONDS = 25 * 60; // 25 minutes
const PERSIST_INTERVAL_MS = 60 * 1000; // Persist every 60 seconds

interface UseCodingPhaseTimerOptions {
  interviewId?: string;
  initialElapsedSeconds?: number;
  enabled?: boolean;
  onExpire?: () => void;
}

interface UseCodingPhaseTimerReturn {
  /** Remaining time in seconds */
  remainingSeconds: number;
  /** Formatted remaining time (MM:SS) */
  remainingLabel: string;
  /** Whether the timer has expired */
  isExpired: boolean;
  /** Current elapsed seconds */
  elapsedSeconds: number;
}

/**
 * Hook to manage the 25-minute coding phase timer.
 * - Counts down from 25 minutes minus any previously elapsed time
 * - Persists elapsed time to the database every 60 seconds
 * - Calls onExpire callback when timer reaches 0
 */
export function useCodingPhaseTimer(options: UseCodingPhaseTimerOptions): UseCodingPhaseTimerReturn {
  const {
    interviewId,
    initialElapsedSeconds = 0,
    enabled = true,
    onExpire,
  } = options;

  const [elapsedSeconds, setElapsedSeconds] = useState<number>(initialElapsedSeconds);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const startTimeRef = useRef<number | null>(null);
  const lastPersistedRef = useRef<number>(initialElapsedSeconds);
  const elapsedRef = useRef<number>(initialElapsedSeconds);
  const onExpireRef = useRef(onExpire);
  const hasCalledExpireRef = useRef(false);

  // Keep onExpire ref up to date
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Initialize timer
  useEffect(() => {
    if (!enabled || !interviewId) {
      return;
    }

    // Reset state when initialElapsedSeconds changes (e.g., on resume)
    setElapsedSeconds(initialElapsedSeconds);
    elapsedRef.current = initialElapsedSeconds;
    lastPersistedRef.current = initialElapsedSeconds;
    hasCalledExpireRef.current = false;

    // Check if already expired
    if (initialElapsedSeconds >= CODING_PHASE_DURATION_SECONDS) {
      setIsExpired(true);
      if (!hasCalledExpireRef.current) {
        hasCalledExpireRef.current = true;
        onExpireRef.current?.();
      }
      return;
    }

    setIsExpired(false);
    startTimeRef.current = Date.now() - initialElapsedSeconds * 1000;

    // Timer interval - updates every second
    const timerInterval = setInterval(() => {
      const startedAt = startTimeRef.current;
      if (!startedAt) return;

      const newElapsed = Math.floor((Date.now() - startedAt) / 1000);
      setElapsedSeconds(newElapsed);
      elapsedRef.current = newElapsed;

      // Check for expiration
      if (newElapsed >= CODING_PHASE_DURATION_SECONDS && !hasCalledExpireRef.current) {
        setIsExpired(true);
        hasCalledExpireRef.current = true;
        onExpireRef.current?.();
      }
    }, 1000);

    return () => {
      clearInterval(timerInterval);
    };
  }, [enabled, interviewId, initialElapsedSeconds]);

  // Persist elapsed time to database every 60 seconds
  useEffect(() => {
    if (!enabled || !interviewId || isExpired) {
      return;
    }

    const persistInterval = setInterval(() => {
      const currentElapsed = elapsedRef.current;
      // Persist at most once per minute. Also avoid redundant writes.
      if (currentElapsed !== lastPersistedRef.current) {
        lastPersistedRef.current = currentElapsed;
        updateCodingPhaseTime(interviewId, currentElapsed).catch((error) => {
          console.error('[CodingPhaseTimer] Failed to persist elapsed time:', error);
        });
      }
    }, PERSIST_INTERVAL_MS);

    return () => {
      clearInterval(persistInterval);
    };
  }, [enabled, interviewId, isExpired]);

  // Persist on unmount if there's unsaved progress
  useEffect(() => {
    if (!enabled || !interviewId) {
      return;
    }

    return () => {
      const currentElapsed = elapsedRef.current;
      if (currentElapsed > lastPersistedRef.current) {
        // Fire and forget - we're unmounting
        updateCodingPhaseTime(interviewId, currentElapsed).catch(() => {
          // Ignore errors on unmount
        });
      }
    };
  }, [enabled, interviewId]);

  const remainingSeconds = useMemo(() => {
    return Math.max(0, CODING_PHASE_DURATION_SECONDS - elapsedSeconds);
  }, [elapsedSeconds]);

  const remainingLabel = useMemo(() => {
    const mins = Math.floor(remainingSeconds / 60);
    const secs = remainingSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, [remainingSeconds]);

  return {
    remainingSeconds,
    remainingLabel,
    isExpired,
    elapsedSeconds,
  };
}

export default useCodingPhaseTimer;

